import type { WorkflowInvoiceLine, WorkflowInvoiceSourceLineCandidate } from '../../types/workflowInvoice';
import { asQuantityUnit, buildQuantitySetFromPair, quantitySetToFields } from '../../utils/commercialQuantity';
import { extractWarehouseQuantitySetPatch, type WarehouseQuantityPatch } from '../warehouse/warehouseQuantityConversion';

export type SourceQuantitySet = NonNullable<WorkflowInvoiceSourceLineCandidate['sourceQuantitySet']>;

/**
 * Persisted physical set of a source order/ZD line. A line saved before the
 * quantity-set era carries only `quantity` + `unit`; that single unit is its
 * whole snapshot. Returns null when nothing physical is persisted.
 */
export function sourceQuantitySetFromLine(line: {
  quantity?: number | null;
  unit?: string | null;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
}): SourceQuantitySet | null {
  const set: SourceQuantitySet = {
    pcs: line.quantityPcs ?? null,
    m2: line.quantityM2 ?? null,
    m3: line.quantityM3 ?? null,
  };
  if (set.pcs != null || set.m2 != null || set.m3 != null) return set;
  const unit = asQuantityUnit(line.unit);
  if (unit && line.quantity != null && Number(line.quantity) > 0) {
    return { ...set, [unit]: Number(line.quantity) };
  }
  return null;
}

/**
 * Drops every derived unit the source line never persisted. The server rejects
 * such units (`INVOICE_QUANTITY_UNRESOLVED`) instead of backfilling them from
 * catalog geometry. Without a known source set the derived set is kept.
 */
export function restrictQuantitySetToSource<T extends Pick<WorkflowInvoiceLine, 'quantityPcs' | 'quantityM2' | 'quantityM3'>>(
  fields: T,
  sourceSet: SourceQuantitySet | null | undefined,
): T {
  if (!sourceSet) return fields;
  return {
    ...fields,
    quantityPcs: sourceSet.pcs == null ? null : fields.quantityPcs,
    quantityM2: sourceSet.m2 == null ? null : fields.quantityM2,
    quantityM3: sourceSet.m3 == null ? null : fields.quantityM3,
  };
}

export function hasInvoicePhysicalQuantitySet(line: Pick<WorkflowInvoiceLine, 'quantityPcs' | 'quantityM2' | 'quantityM3'>): boolean {
  return [line.quantityPcs, line.quantityM2, line.quantityM3].some(value => value != null);
}

/** New modal values carry a pair; this adapter owns its first persisted set. */
export function invoiceQuantityPatch(
  line: WorkflowInvoiceLine,
  patch: WarehouseQuantityPatch,
  sourceSet?: SourceQuantitySet | null,
): Partial<WorkflowInvoiceLine> {
  const existingSet = extractWarehouseQuantitySetPatch(patch);
  const acceptedPair = {
    physicalQuantity: patch.quantity,
    physicalUnit: asQuantityUnit(patch.unit),
    pricingQuantity: patch.pricingQuantity ?? patch.quantity,
    pricingUnit: asQuantityUnit(patch.pricingUnit ?? patch.unit),
    conversionUnit: patch.conversionUnit,
    conversionQuantity: patch.conversionQuantity ?? undefined,
  };
  const firstSet = hasInvoicePhysicalQuantitySet(existingSet)
    ? null
    : buildQuantitySetFromPair(acceptedPair, line);
  return {
    quantity: patch.quantity,
    unit: acceptedPair.physicalUnit,
    conversionUnit: patch.conversionUnit,
    conversionQuantity: patch.conversionQuantity ?? null,
    ...restrictQuantitySetToSource(firstSet ? quantitySetToFields(firstSet) : existingSet, sourceSet),
    commercialQuantity: patch.pricingQuantity ?? null,
    commercialUnit: patch.pricingUnit ?? null,
  };
}
